import React, { useEffect, useState } from "react";
import { Row, Col, Table, Spinner } from "react-bootstrap";
import { Autocomplete, TextField, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Axios } from "api/axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { showCustomSwal } from "helpers/swalHelper";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import LoadingSpinner from "components/LoadingSpinner";
import { autocompleteDropdownStyles } from "Constants/Style";
import { MecordColors } from "Constants/Color";

interface RowData {
  id: string;
  organization: string;
  organization_name: string;
  department: string;
  department_name: string;
  team: string;
  team_name: string;
  users: string;
  username: string;
}

const ConfigureWarehouse = () => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [formData, setFormData] = useState<any>({
    organization: null,
    department: null,
    team: null,
    user: null,
  });

  const [orgOptions, setOrgOptions] = useState<any[]>([]);
  const [deptOptions, setDeptOptions] = useState<any[]>([]);
  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) toast.error("Warehouse ID missing in URL");
  }, [id]);

  const fetchConfiguredRows = async (warehouseId: string) => {
    setIsLoading(true);
    try {
      const res = await Axios.get(
        API_ENDPOINT.MASTERS.WAREHOUSE.CONFIGURATION.LIST(warehouseId)
      );
      const resData = res?.data?.data || res?.data;
      if (Array.isArray(resData)) {
        const formatted = resData.map((item: any) => ({
          id: item?.configure_warehouse_id,
          organization: item?.user?.organization.organization_id || "",
          organization_name: item?.user?.organization.organization_name || "",
          department: item?.user?.department.department_id || "",
          department_name: item?.user?.department.department_name || "",
          team: item?.user?.team.team_id || "",
          team_name: item?.user?.team.team_name || "",
          users: item?.user?.user_id || "",
          username: item?.user?.username || "",
        }));
        setRows(formatted);
      }
    } catch (err) {
      console.error("Error fetching configured rows:", err);
      toast.error("Failed to fetch warehouse configurations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchConfiguredRows(id);
  }, [id]);

  const fetcOrg = async () => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.USER_MANAGEMENT.ORGANIZATION.DROPDOWN
      );
      setOrgOptions(response?.data?.status === 200 ? response.data.data : []);
    } catch (err) {
      console.error("Error fetching orgs:", err);
    }
  };

  const fetcDep = async (orgId: string) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.USER_MANAGEMENT.DEPARTMENT.BY_ORG(orgId)
      );
      setDeptOptions(response?.data?.status === 200 ? response.data.data : []);
    } catch (err) {
      console.error("Error fetching departments:", err);
    }
  };

  const fetcTeam = async (depId: string) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.USER_MANAGEMENT.TEAM.BY_DEPARTMENT(depId)
      );
      setTeamOptions(response?.data?.data || []);
    } catch (err) {
      console.error("Error fetching teams:", err);
    }
  };

  const fetchUser = async (data: any) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.USER_MANAGEMENT.USER.BY_TEAM(data.team_id),
        {}
      );
      setUserOptions(response?.data?.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetcOrg();
  }, []);

  // Handle organization change or clear
  useEffect(() => {
    if (formData?.organization?.organization_id) {
      fetcDep(formData.organization.organization_id);
      setFormData((prev: any) => ({
        ...prev,
        department: null,
        team: null,
        user: null,
      }));
      setDeptOptions([]);
      setTeamOptions([]);
      setUserOptions([]);
    } else {
      setDeptOptions([]);
      setTeamOptions([]);
      setUserOptions([]);
      setFormData((prev: any) => ({
        ...prev,
        department: null,
        team: null,
        user: null,
      }));
    }
  }, [formData?.organization]);

  // Handle department change or clear
  useEffect(() => {
    if (formData?.department?.department_id) {
      fetcTeam(formData.department.department_id);
      setFormData((prev: any) => ({
        ...prev,
        team: null,
        user: null,
      }));
      setTeamOptions([]);
      setUserOptions([]);
    } else {
      setTeamOptions([]);
      setUserOptions([]);
      setFormData((prev: any) => ({
        ...prev,
        team: null,
        user: null,
      }));
    }
  }, [formData?.department]);

  // Handle team change or clear

  useEffect(() => {
    if (
      formData?.team?.team_id &&
      formData?.organization?.organization_id &&
      formData?.department?.department_id
    ) {
      fetchUser({
        organization_id: formData.organization.organization_id,
        department_id: formData.department.department_id,
        team_id: formData.team.team_id,
      });
      setFormData((prev: any) => ({ ...prev, user: null }));
      setUserOptions([]);
    } else {
      setUserOptions([]);
      setFormData((prev: any) => ({ ...prev, user: null }));
    }
  }, [formData?.team]);

  const resetForm = () => {
    setDeptOptions([]);
    setTeamOptions([]);
    setUserOptions([]);
    setTimeout(() => {
      setFormData({
        organization: null,
        department: null,
        team: null,
        user: null,
      });
    }, 0);
  };

  const handleAdd = async () => {
    if (!id) return toast.error("Warehouse ID is missing in URL");

    const { organization, department, team, user } = formData;
    if (!organization || !department || !team || !user) {
      return toast.error("Please select all fields");
    }

    const exists = rows.some(
      (row) =>
        row.organization_name === organization.organization_id &&
        row.department === department.department_id &&
        row.team === team.team_id &&
        row.users === user.user_id
    );
    if (exists)
      return toast.warning("Duplicate warehouse configuration is not allowed.");

    setIsSubmitting(true);
    try {
      const payload = {
        warehouse_id: id,
        organization_name: organization.organization_id,
        department: department.department_id,
        team: team.team_id,
        user_id: user.user_id,
      };

      const res = await Axios.post(
        API_ENDPOINT.MASTERS.WAREHOUSE.CONFIGURATION.CREATE(id),
        payload
      );

      if (res?.data?.status === 200 || res?.status === 201) {
        toast.success("Warehouse configured successfully!");
        fetchConfiguredRows(id);
        resetForm();
      } else {
        toast.error("Failed to add row");
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Something went wrong";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async (idToDelete: string) => {
    const result = await showCustomSwal({
      icon: "warning",
      title: "Are you sure?",
      text: "Are you sure you want to delete this warehouse configuration? This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setIsDeleting(true);
      try {
        const res = await Axios.delete(
          API_ENDPOINT.MASTERS.WAREHOUSE.CONFIGURATION.DELETE(
            idToDelete.toString()
          )
        );

        if (res?.data?.message) {
          toast.success(res.data.message);
          if (id) fetchConfiguredRows(id);
          setRows((prev) => prev.filter((row: any) => row.id !== idToDelete));
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to delete configuration"
        );
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleAutocompleteChange = (key: string, value: any) => {
    let updatedFormData = { ...formData };

    if (key === "organization") {
      if (!value) {
        // Clearing organization clears everything
        updatedFormData = {
          organization: null,
          department: null,
          team: null,
          user: null,
        };
        setDeptOptions([]);
        setTeamOptions([]);
        setUserOptions([]);
        fetcOrg(); // refetch org options
      } else {
        updatedFormData = {
          organization: value,
          department: null,
          team: null,
          user: null,
        };
        setDeptOptions([]);
        setTeamOptions([]);
        setUserOptions([]);
        fetcDep(value.organization_id); // fetch deps for new org
      }
    }

    if (key === "department") {
      if (!value) {
        // Clearing department clears dept, team, user
        updatedFormData = {
          ...updatedFormData,
          department: null,
          team: null,
          user: null,
        };
        setTeamOptions([]);
        setUserOptions([]);
        if (formData.organization?.organization_id) {
          fetcDep(formData.organization.organization_id); // refetch deps for selected org
        }
      } else {
        updatedFormData = {
          ...updatedFormData,
          department: value,
          team: null,
          user: null,
        };
        setTeamOptions([]);
        setUserOptions([]);
        fetcTeam(value.department_id); // fetch teams for new dept
      }
    }

    if (key === "team") {
      if (!value) {
        // Clearing team clears team and user only
        updatedFormData = {
          ...updatedFormData,
          team: null,
          user: null,
        };
        setUserOptions([]);
        if (
          formData.organization?.organization_id &&
          formData.department?.department_id
        ) {
          fetcTeam(formData.department.department_id); // refetch teams for current dept
        }
      } else {
        updatedFormData = {
          ...updatedFormData,
          team: value,
          user: null,
        };
        setUserOptions([]);
        fetchUser({
          organization_id: formData.organization.organization_id,
          department_id: formData.department.department_id,
          team_id: value.team_id,
        }); // fetch users for team
      }
    }

    if (key === "user") {
      if (!value) {
        updatedFormData = {
          ...updatedFormData,
          user: null,
        };
      } else {
        // Match the actual object from options array
        const matchedUser = userOptions.find(
          (opt) => opt.user_id === value.user_id
        );
        updatedFormData = {
          ...updatedFormData,
          user: matchedUser || value,
        };
      }
    }

    setFormData(updatedFormData);
  };

  return (
    <PageContainer>
      <PageBreadcrumb
        title="Configure Warehouse"
        titleLink="/master/warehouse-list"
        breadcrumbs={[
          { label: "Warehouse", to: "/master/warehouse-list" },
          { label: "Configure Warehouse", to: "" },
        ]}
        backNavigationPath={-1}
      />

      <hr />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="mt-4">
            <Table
              bordered
              // hover
              responsive
              className="align-middle text-center"
              style={{
                backgroundColor: "#fff",
                borderRadius: "0px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <thead>
                <tr style={{ backgroundColor:  MecordColors.LightGrayColor }}>
                  <th>
                    Sr No.
                  </th>
                  <th>
                    organization
                  </th>
                  <th>
                    Department
                  </th>
                  <th>
                    Team
                  </th>
                  <th>
                    User
                  </th>
                  <th>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: "#f8f9fa" }}>
                  <td
                    style={{
                      padding: "12px",
                      verticalAlign: "middle",
                      fontWeight: "600",
                    }}
                  >
                    New
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Autocomplete
                      options={orgOptions}
                      value={formData.organization}
                      onChange={(e, val) =>
                        handleAutocompleteChange("organization", val)
                      }
                      onInputChange={(e, inputValue) => {
                        if (!inputValue)
                          handleAutocompleteChange("organization", null);
                      }}
                      getOptionLabel={(option) =>
                        option?.organization_name || ""
                      }
                      isOptionEqualToValue={(o, v) =>
                        o.organization_id === v.organization_id
                      }
                      renderOption={(props, option) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt) =>
                            typeof opt === "string"
                              ? opt
                              : String(opt?.organization_name)
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          size="small"
                          placeholder="Select organization"
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                    />
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Autocomplete
                      options={deptOptions}
                      value={formData.department}
                      onChange={(e, val) =>
                        handleAutocompleteChange("department", val)
                      }
                      onInputChange={(e, inputValue) => {
                        if (!inputValue)
                          handleAutocompleteChange("department", null);
                      }}
                      getOptionLabel={(option) => option?.department_name || ""}
                      isOptionEqualToValue={(o, v) =>
                        o.department_id === v.department_id
                      }
                      renderOption={(props, option) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt) =>
                            typeof opt === "string"
                              ? opt
                              : String(opt?.department_name)
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          size="small"
                          placeholder="Select Department"
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                    />
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Autocomplete
                      options={teamOptions}
                      value={formData.team}
                      onChange={(e, val) =>
                        handleAutocompleteChange("team", val)
                      }
                      onInputChange={(e, inputValue) => {
                        if (!inputValue) handleAutocompleteChange("team", null);
                      }}
                      getOptionLabel={(option) => option?.team_name || ""}
                      isOptionEqualToValue={(o, v) => o.team_id === v.team_id}
                      renderOption={(props, option) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt) =>
                            typeof opt === "string"
                              ? opt
                              : String(opt?.team_name)
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          size="small"
                          placeholder="Select Team"
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                    />
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Autocomplete
                      options={userOptions}
                      value={formData.user}
                      onChange={(e, val) =>
                        handleAutocompleteChange("user", val)
                      }
                      onInputChange={(e, inputValue) => {
                        if (!inputValue) handleAutocompleteChange("user", null);
                      }}
                      getOptionLabel={(option) => option?.username || ""}
                      isOptionEqualToValue={(o, v) => o.user_id === v.user_id}
                      renderOption={(props, option) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt) =>
                            typeof opt === "string"
                              ? opt
                              : String(opt?.username)
                        )
                      }
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          size="small"
                          placeholder="Select User"
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                    />
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "middle" }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleAdd}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: "#AB2123",
                        borderColor: "#AB2123",
                        minWidth: "80px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#891A1C";
                        e.currentTarget.style.borderColor = "#891A1C";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#AB2123";
                        e.currentTarget.style.borderColor = "#AB2123";
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Adding...
                        </>
                      ) : (
                        "Add"
                      )}
                    </button>
                  </td>
                </tr>

                {rows.map((row: any, idx: number) => (
                  <tr
                    key={row.id}
                    style={{
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }}
                  >
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      {row?.organization_name}
                    </td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      {row.department_name}
                    </td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      {row.team_name}
                    </td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      {row.username}
                    </td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      <IconButton
                        onClick={() => handleDeleteClick(row?.id)}
                        size="small"
                        color="error"
                        disabled={isDeleting}
                        style={{
                          color: "#dc3545",
                          transition: "transform 0.2s ease",
                          opacity: isDeleting ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!isDeleting) {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
            <CancelButton
              redirectTo="/master/warehouse-list"
              disabled={isSubmitting}
              showConfirmation={false}
            />
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default ConfigureWarehouse;
